import { FC } from 'react';

import { Button, Flex, Link } from '@rover/kibble/core';
import { Props as FlexProps } from '@rover/kibble/core/Flex/Flex';
import useLocationContext from '@rover/react-lib/src/context/useLocationContext';
import { INCLUDE_NEARBY_TRAINER_COUNT } from '@rover/react-lib/src/pages/training/constants/params';
import TrainingUrls from '@rover/react-lib/src/pages/training/constants/urls';
import { ShowMoreTrainersLinkClicked } from '@rover/react-lib/src/pages/training/events/analyticsEvents';
import { useApiNearbyTrainerCountRetrieve } from '@rover/rsdk/src/apiClient/latest/api-nearby-trainer-count-retrieve';
import getUserAgentFlags from '@rover/shared/js/utils/getUserAgentFlags';
import { storeCrossPageAnalyticsEvent } from '@rover/utilities/fireCrossPageEvent';

import {
  getShouldFetchNearbyTrainerCounts,
  getShouldUseNearbyTrainerCountQueryParam,
  parseForceSeoLandingQueryParam,
  parseNearbyTrainerCountQueryParam,
} from './utils';

type Props = {
  providerSlug: string;
  zip: string;
} & FlexProps;

const ShowMoreTrainersLink: FC<Props> = ({ providerSlug, zip, ...props }) => {
  const { isDesktop } = getUserAgentFlags();
  const { search: initialQueryParams } = useLocationContext();

  const nearbyTrainerCountQueryParam = parseNearbyTrainerCountQueryParam(initialQueryParams);
  const shouldUseNearbyTrainerCountQueryParam = getShouldUseNearbyTrainerCountQueryParam(
    nearbyTrainerCountQueryParam
  );
  const forceSeoLanding = parseForceSeoLandingQueryParam(initialQueryParams);
  const shouldFetchNearbyTrainerCounts = getShouldFetchNearbyTrainerCounts(
    shouldUseNearbyTrainerCountQueryParam,
    forceSeoLanding
  );

  const { data: nearbyTrainerCountResponse } = useApiNearbyTrainerCountRetrieve(
    providerSlug,
    {
      searchNearUser: false,
    },
    {
      query: {
        enabled: shouldFetchNearbyTrainerCounts,
      },
    }
  );
  const { data: nearUserNearbyTrainerCountResponse } = useApiNearbyTrainerCountRetrieve(
    providerSlug,
    {
      searchNearUser: true,
    },
    {
      query: {
        enabled: shouldFetchNearbyTrainerCounts,
      },
    }
  );

  const shouldSearchNearTrainer = zip && (nearbyTrainerCountResponse?.nearbyTrainerCount || 0) > 0;

  const nearbyTrainerQueryCount = shouldSearchNearTrainer
    ? nearbyTrainerCountResponse?.nearbyTrainerCount
    : nearUserNearbyTrainerCountResponse?.nearbyTrainerCount;

  const nearbyTrainerCount = shouldUseNearbyTrainerCountQueryParam
    ? nearbyTrainerCountQueryParam
    : nearbyTrainerQueryCount;

  const searchParams = new URLSearchParams({
    ...(shouldSearchNearTrainer
      ? {
          location: zip,
          location_type: 'zip-code',
        }
      : {}),
    ...(!isDesktop ? { [INCLUDE_NEARBY_TRAINER_COUNT]: 'true' } : {}),
  });

  if (!nearbyTrainerCount) return null;

  // TODO (DEV-104195): Translate
  const textContent =
    nearbyTrainerCount > 5
      ? `Show ${nearbyTrainerCount} trainers in your area`
      : 'Show all trainers in your area';

  return (
    <Flex width="100%" justifyContent="center" alignItems="center" {...props}>
      {shouldUseNearbyTrainerCountQueryParam ? (
        <Button
          onClick={() => {
            storeCrossPageAnalyticsEvent(new ShowMoreTrainersLinkClicked());
            // Will always be true, just needed to suppress linter warning
            if (process.env.JS_ENV_CLIENT) {
              window.history.back();
            }
          }}
          variant="flat"
          padding="0x"
        >
          {textContent}
        </Button>
      ) : (
        <Link
          target={isDesktop ? '_blank' : undefined}
          onPress={() => {
            storeCrossPageAnalyticsEvent(new ShowMoreTrainersLinkClicked());
          }}
          href={`${TrainingUrls.SEARCH}${`?${searchParams.toString()}`}`}
          sx={{
            '& > span': {
              textDecoration: 'none',
            },
          }}
          fontWeight="semibold"
        >
          {textContent}
        </Link>
      )}
    </Flex>
  );
};

export default ShowMoreTrainersLink;
